import type {DownloadPurchaseResult, GA4PurchaseEventParams, SubscriptionTrackingData} from '@Components/checkout-flow/checkout-flow.types';
import {
  CACHE_EXPIRY_ONE_HOUR_MS,
  GA4_EVENT_NAME_NEW_SUBSCRIPTION_START,
  GA4_EVENT_NAME_TRIAL_START,
  ONLINE_ADS_EVENT_TYPE,
  PREMIUM_UPSELL_DIALOG_SHOWN_COOKIE,
  PURCHASE_ORDER_TYPE_SUBSCRIPTION,
  SUBSCRIPTION_PURCHASE_TRACKING_CACHE_KEY,
} from '@Components/checkout-flow/checkout-flow.types';
import {readCookie} from '@Utils/cookie.util';
import {GA4EventParamName} from '@Libraries/ga-events';

interface PremiumPurchaseTrackingInput {
  ga4PurchaseEventParams: GA4PurchaseEventParams | null;
  trackingData: SubscriptionTrackingData | null;
  isTrial: boolean;
}

/**
 * A purchase is reported once. The confirmation panel is re-entered on a page reload after checkout and
 * on the ?csid= redirect return, so the id of the last reported purchase is remembered for an hour and a
 * repeat of that same purchase is dropped.
 *
 * Keyed on the purchase id rather than a flag: a flag cannot tell a re-entry apart from a second, genuine
 * purchase in the same hour, so it would trade double counting for under counting.
 */
export const trackPremiumPurchase = ({ga4PurchaseEventParams, trackingData, isTrial}: PremiumPurchaseTrackingInput): void => {
  if (isTrial) {
    trackTrialStart();
    return;
  }

  if (!trackingData?.idSubscriptionPurchase || hasAlreadyTrackedSubscription(trackingData.idSubscriptionPurchase)) {
    return;
  }

  if (ga4PurchaseEventParams !== null) {
    window.PMW.gtm.trackGA4PurchaseEvent(ga4PurchaseEventParams, trackingData.sutm);
  }

  const isResubscription = trackingData.isResubscription === 1;
  if (!isResubscription) {
    window.PMW.gtm.trackGA4CustomEvent(GA4_EVENT_NAME_NEW_SUBSCRIPTION_START, getNewSubscriptionStartParams(trackingData));
  }

  if (trackingData.orderValue) {
    const eventType = isResubscription ? ONLINE_ADS_EVENT_TYPE.RESUBSCRIBED : ONLINE_ADS_EVENT_TYPE.SUBSCRIPTION_PURCHASE;
    window.PMW.trackOnlineAdsEvent(eventType, PURCHASE_ORDER_TYPE_SUBSCRIPTION, String(trackingData.orderValue), String(trackingData.idSubscriptionPurchase));
  }

  window.PMW.setCacheItem(SUBSCRIPTION_PURCHASE_TRACKING_CACHE_KEY, String(trackingData.idSubscriptionPurchase), CACHE_EXPIRY_ONE_HOUR_MS);
};

/**
 * Compared as text on purpose: the id is declared a number but arrives from the endpoint as a string, so a
 * strict comparison of the raw values would hold only for as long as both sides keep the same accidental
 * type. Normalising both ends makes the guard independent of that.
 */
const hasAlreadyTrackedSubscription = (idSubscriptionPurchase: number): boolean => {
  return window.PMW.getCacheItem(SUBSCRIPTION_PURCHASE_TRACKING_CACHE_KEY) === String(idSubscriptionPurchase);
};

const getNewSubscriptionStartParams = (trackingData: SubscriptionTrackingData): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    [GA4EventParamName.TYPE]: trackingData.planType,
    [GA4EventParamName.TERM]: trackingData.term,
    [GA4EventParamName.SEATS]: trackingData.seats,
  };

  if (trackingData.upsellDialogName) {
    params[GA4EventParamName.SOURCE] = trackingData.upsellDialogName;
  } else if (trackingData.ga4EventSource) {
    params[GA4EventParamName.SOURCE] = trackingData.ga4EventSource;
  }

  return params;
};

export const trackTrialStart = (): void => {
  window.PMW.trackOnlineAdsEvent(ONLINE_ADS_EVENT_TYPE.TRIAL_START, PURCHASE_ORDER_TYPE_SUBSCRIPTION);

  const upsellDialogName = readCookie(PREMIUM_UPSELL_DIALOG_SHOWN_COOKIE) ?? '';
  if (upsellDialogName !== '') {
    window.PMW.gtm.trackGA4CustomEvent(GA4_EVENT_NAME_TRIAL_START, {[GA4EventParamName.SOURCE]: upsellDialogName});
  } else {
    window.PMW.gtm.trackGA4CustomEvent(GA4_EVENT_NAME_TRIAL_START);
  }

  void window.PMW.writeLocal('checkout/recordGA4TrialStartEvent', {eventName: GA4_EVENT_NAME_TRIAL_START});
};

export const trackDownloadPurchase = (result: DownloadPurchaseResult): void => {
  window.PMW.trackOnlineAdsEvent(ONLINE_ADS_EVENT_TYPE.DOWNLOAD_PURCHASE, result.orderType, String(result.orderValue ?? ''), result.idOrder ?? undefined);
};

export const trackGA4Purchase = (ga4PurchaseEventParams: GA4PurchaseEventParams | null | undefined): void => {
  if (ga4PurchaseEventParams === null || ga4PurchaseEventParams === undefined) {
    return;
  }
  window.PMW.gtm.trackGA4PurchaseEvent(ga4PurchaseEventParams);
};
